import { requests } from '@fable/api'
import { OnClickShareCtaProps, ShareContent } from './types'
import {
  BookListResult,
  ShareObjectData,
  ShareObjectType,
  Quote,
  Review,
  UserPost,
} from '@fable/types'
import {
  ActivityBooklist,
  ActivityPost,
  ActivityQuote,
  ActivityReview,
} from 'components/activity'
import { pageMetadata } from 'routes/pageMetadata'

export const getShareData = async ({
  type,
  id,
}: {
  type: ShareObjectType
  id: string
}) => {
  if (type === 'user-post' || type === 'editorial-post') {
    return await requests.getUserPost(id)
  }

  switch (type) {
    case 'list':
      return await requests.getBookList(id)
    case 'review':
      return await requests.getReview(id)
    case 'quote':
      return await requests.getQuote(id)
  }
}

export const getShareContent = ({
  id,
  data,
  type,
  onClickCta,
}: {
  id: string
  data: ShareObjectData
  type: ShareObjectType
  onClickCta: (props: OnClickShareCtaProps) => void
}): ShareContent => {
  const author = getShareAuthor({ data, type })
  const firstName = author?.display_name?.split(' ')[0] || ''

  const sharedProps = {
    onClickLike: () =>
      onClickCta({
        id,
        type,
        cta: 'like',
        title: 'Like what you see?',
        description: `Create a free Fable account to finish liking ${
          !!firstName?.length ? `${firstName}’s` : 'this'
        } post!`,
      }),
    onClickComment: () =>
      onClickCta({
        id,
        type,
        cta: 'comment',
        title: 'Share your thoughts',
        description: 'Create a free Fable account to join the conversation!',
      }),
  }

  switch (type) {
    case 'list': {
      const booklist = data as BookListResult

      return {
        metadata: pageMetadata.bookList(booklist),
        content: (
          <ActivityBooklist
            booklist={booklist}
            onClickLink={() =>
              onClickCta({
                id,
                type,
                cta: 'link',
                skipModalOnMobile: true,
              })
            }
            {...sharedProps}
          />
        ),
        valuePropsId: '2IuXcSiMNbezpKCh57Dqcd',
      }
    }

    case 'user-post': {
      const post = data as UserPost

      return {
        metadata: pageMetadata.userPost(post),
        content: (
          <ActivityPost
            post={post}
            options={{ stacked: true }}
            {...sharedProps}
          />
        ),
        valuePropsId: '6kKmk4KwgP93pm42ANt1R5',
      }
    }

    case 'editorial-post': {
      const post = data as UserPost

      return {
        metadata: pageMetadata.userPost(post),
        content: (
          <ActivityPost
            post={post}
            options={{
              stacked: true,
              onCardClick: () =>
                onClickCta({
                  id,
                  type,
                  cta: 'post-object-card',
                  skipModalOnMobile: true,
                }),
            }}
            showFullPost
            {...sharedProps}
          />
        ),
        valuePropsId: '6QOxpqNaDjEy68qYgAbfNK',
      }
    }

    case 'review': {
      const review = data as Review

      return {
        metadata: pageMetadata.review(review),
        content: (
          <ActivityReview
            review={review}
            onClickFullReview={() =>
              onClickCta({
                id,
                type,
                cta: 'review-preview',
                title: "There's more to see in the app",
                description: `Create a free Fable account to see more from ${firstName}!`,
              })
            }
            onClickLink={() =>
              onClickCta({
                id,
                type,
                cta: 'link',
                skipModalOnMobile: true,
              })
            }
            {...sharedProps}
          />
        ),
        valuePropsId: '6jtnbN3E014Wc7HadS1RM7',
      }
    }

    case 'quote':
      const quote = data as Quote

      return {
        metadata: pageMetadata.quote(quote),
        content: <ActivityQuote quote={quote} {...sharedProps} />,
        valuePropsId: 'sKXTjdUhGqrMzHwZHCa5X',
      }
  }
}

export const getShareAuthor = ({
  data,
  type,
}: {
  data: ShareObjectData
  type: ShareObjectType
}) => {
  switch (type) {
    case 'list': {
      const booklist = data as BookListResult
      return booklist.user || {}
    }
    case 'user-post': {
      const post = data as UserPost
      return post.author
    }
    case 'editorial-post': {
      const post = data as UserPost
      return post.author
    }
    case 'review': {
      const review = data as Review
      return review.user
    }
    case 'quote':
      const quote = data as Quote
      return quote.user
  }
}

export const isValidType = (type: ShareObjectType) => {
  const validTypes: ShareObjectType[] = [
    'list',
    'user-post',
    'editorial-post',
    'review',
    'quote',
  ]

  return validTypes.includes(type)
}
